import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Coins, Diamond, ShoppingBag, Palette, Crown, Frame, Puzzle, Filter, Eye, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { useCurrency } from '@/hooks/useCurrency';
import { useShop, ShopItem } from '@/hooks/useShop';
import { useCosmetics, BoardTheme } from '@/hooks/useCosmetics';
import { ShopPreviewPanel } from '@/components/shop/ShopPreviewPanel';
import { CollectionSummary } from '@/components/shop/CollectionSummary';
import { PieceStyle } from '@/lib/pieces';

const categoryIcons: Record<string, React.ReactNode> = {
  board_theme: <Palette className="h-5 w-5" />,
  piece_style: <Puzzle className="h-5 w-5" />,
  avatar_accessory: <Crown className="h-5 w-5" />,
  profile_decoration: <Frame className="h-5 w-5" />,
};

const categoryLabels: Record<string, string> = {
  board_theme: 'Board Themes',
  piece_style: 'Piece Styles',
  avatar_accessory: 'Avatar Accessories',
  profile_decoration: 'Profile Decorations',
};

interface ShopItemCardProps {
  item: ShopItem;
  isOwned: boolean;
  isEquipped: boolean;
  onPurchase: () => void;
  onEquip: () => void;
  onPreview: () => void;
  purchasing: boolean;
}

function ShopItemCard({ 
  item, 
  isOwned, 
  isEquipped,
  onPurchase, 
  onEquip,
  onPreview,
  purchasing 
}: ShopItemCardProps) {
  const isFree = item.price_coins === 0 && item.price_gems === 0;

  return (
    <Card 
      className={`relative overflow-hidden transition-all duration-300 hover:shadow-lg group ${
        isEquipped 
          ? 'border-primary ring-2 ring-primary/30 bg-primary/5 shop-item-equipped' 
          : isOwned 
            ? 'border-green-500/50 bg-green-500/5 shop-item-owned' 
            : 'hover:border-primary/50'
      }`}
      onMouseEnter={onPreview}
    >
      {/* Owned checkmark ribbon */}
      {isOwned && !isEquipped && (
        <div className="absolute top-0 right-0 w-8 h-8 overflow-hidden z-10">
          <div className="absolute top-0 right-0 w-0 h-0 border-t-[32px] border-t-green-500 border-l-[32px] border-l-transparent" />
          <Check className="absolute top-1 right-1 h-3 w-3 text-white" />
        </div>
      )}

      {/* Equipped badge */}
      {isEquipped && (
        <Badge className="absolute top-2 left-2 bg-primary text-primary-foreground z-10 animate-pulse">
          <Check className="h-3 w-3 mr-1" />
          Equipped
        </Badge>
      )}
      
      {/* Preview button on hover */}
      <Button
        variant="ghost"
        size="icon"
        className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity z-10 h-7 w-7 bg-background/80 backdrop-blur-sm"
        onClick={(e) => {
          e.stopPropagation();
          onPreview();
        }}
      >
        <Eye className="h-4 w-4" />
      </Button>
      
      {/* Preview Area */}
      <div className={`h-24 flex items-center justify-center transition-all duration-300 ${
        isEquipped 
          ? 'bg-gradient-to-br from-primary/20 to-primary/5' 
          : isOwned
            ? 'bg-gradient-to-br from-green-500/10 to-green-500/5'
            : 'bg-gradient-to-br from-muted to-muted/50 group-hover:from-primary/10 group-hover:to-muted/50'
      }`}>
        {item.preview_image ? (
          <img src={item.preview_image} alt={item.name} className="h-16 w-16 object-contain transition-transform group-hover:scale-110" />
        ) : (
          <div className="text-4xl opacity-50 transition-transform group-hover:scale-110">
            {item.category === 'board_theme' && '🎨'}
            {item.category === 'piece_style' && '♟️'}
            {item.category === 'avatar_accessory' && '👑'}
            {item.category === 'profile_decoration' && '🖼️'}
          </div>
        )}
      </div>
      
      <CardContent className="p-4 space-y-3">
        <div>
          <h3 className="font-semibold">{item.name}</h3>
          <p className="text-xs text-muted-foreground line-clamp-2">{item.description}</p>
        </div>
        
        {/* Price */}
        <div className="flex items-center gap-2">
          {isFree ? (
            <Badge variant="secondary">Free</Badge>
          ) : isOwned ? (
            <Badge variant="outline" className="text-green-600 border-green-500/50">
              <Check className="h-3 w-3 mr-1" />
              Owned
            </Badge>
          ) : (
            <>
              {item.price_coins > 0 && (
                <div className="flex items-center gap-1 text-yellow-500">
                  <Coins className="h-4 w-4" />
                  <span className="font-medium">{item.price_coins}</span>
                </div>
              )}
              {item.price_gems > 0 && (
                <div className="flex items-center gap-1 text-blue-500">
                  <Diamond className="h-4 w-4" />
                  <span className="font-medium">{item.price_gems}</span>
                </div>
              )}
            </>
          )}
        </div>
        
        {/* Action Button */}
        {isOwned ? (
          isEquipped ? (
            <Button 
              variant="outline" 
              size="sm" 
              className="w-full border-primary text-primary hover:bg-primary/10 cursor-default"
              disabled
            >
              <Check className="h-4 w-4 mr-1" />
              Equipped
            </Button>
          ) : (
            <Button 
              size="sm" 
              className="w-full bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70"
              onClick={onEquip}
            >
              Equip Now
            </Button>
          )
        ) : (
          <Button 
            size="sm" 
            className="w-full bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600"
            onClick={onPurchase}
            disabled={purchasing}
          >
            {purchasing ? 'Purchasing...' : isFree ? 'Get Free' : 'Buy Now'}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

export default function Shop() {
  const navigate = useNavigate();
  const { currency } = useCurrency();
  const { items, loading, purchasing, purchaseItem, equipItem, isOwned, isEquipped, getItemsByCategory } = useShop();
  const { boardTheme, pieceStyle, avatarAccessory, profileFrame, defaultBoardTheme } = useCosmetics();
  
  const [confirmItem, setConfirmItem] = useState<ShopItem | null>(null);
  const [activeTab, setActiveTab] = useState('board_theme');
  const [filter, setFilter] = useState<'all' | 'owned' | 'not_owned'>('all');
  
  // Preview state
  const [previewTheme, setPreviewTheme] = useState<BoardTheme | null>(null);
  const [previewPieceStyle, setPreviewPieceStyle] = useState<PieceStyle>('modern');
  const [previewAccessory, setPreviewAccessory] = useState<{ type: string; color?: string } | null>(null);
  const [previewFrame, setPreviewFrame] = useState<{ type: string; color?: string } | null>(null);
  const [isPreviewActive, setIsPreviewActive] = useState(false);

  // Calculate collection stats
  const collectionStats = useMemo(() => {
    const ownedByCategory: Record<string, number> = {};
    const totalByCategory: Record<string, number> = {};
    
    items.forEach(item => {
      totalByCategory[item.category] = (totalByCategory[item.category] || 0) + 1;
      if (isOwned(item.id)) {
        ownedByCategory[item.category] = (ownedByCategory[item.category] || 0) + 1;
      }
    });
    
    return { ownedByCategory, totalByCategory };
  }, [items, isOwned]);

  const handlePurchase = async () => {
    if (!confirmItem) return;
    
    const success = await purchaseItem(confirmItem.id);
    if (success) {
      setConfirmItem(null);
    }
  };

  const handleEquip = async (item: ShopItem) => {
    await equipItem(item.id, item.category);
  };

  const handlePreview = (item: ShopItem) => {
    setIsPreviewActive(true);
    const itemData = item.item_data as Record<string, any>;
    
    switch (item.category) {
      case 'board_theme':
        setPreviewTheme({
          id: item.id,
          name: item.name,
          lightSquare: itemData.lightSquare || defaultBoardTheme.lightSquare,
          darkSquare: itemData.darkSquare || defaultBoardTheme.darkSquare,
          glow: itemData.glow || false,
          glowColor: itemData.glowColor
        });
        break;
      case 'piece_style':
        setPreviewPieceStyle(itemData.style || 'modern');
        break;
      case 'avatar_accessory':
        setPreviewAccessory({
          type: itemData.accessory || itemData.type || 'none',
          color: itemData.color
        });
        break;
      case 'profile_decoration':
        setPreviewFrame({
          type: itemData.frame || itemData.type || 'none',
          color: itemData.color
        });
        break;
    }
  };

  const resetPreview = () => {
    setIsPreviewActive(false);
    setPreviewTheme(null);
    setPreviewPieceStyle('modern');
    setPreviewAccessory(null);
    setPreviewFrame(null);
  };

  const getFilteredItems = (category: string) => {
    const categoryItems = getItemsByCategory(category as ShopItem['category']);
    
    switch (filter) {
      case 'owned':
        return categoryItems.filter(item => isOwned(item.id));
      case 'not_owned':
        return categoryItems.filter(item => !isOwned(item.id));
      default:
        return categoryItems;
    }
  };

  const categories = ['board_theme', 'piece_style', 'avatar_accessory', 'profile_decoration'] as const;

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-muted/20 p-4 md:p-8">
      <div className="max-w-7xl mx-auto space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold flex items-center gap-2">
                <ShoppingBag className="h-6 w-6 text-primary" />
                <span className="bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent">
                  Shop
                </span>
              </h1>
              <p className="text-muted-foreground text-sm">Customize your chess experience</p>
            </div>
          </div>
          
          {/* Currency Display */}
          <div className="flex items-center gap-4 bg-muted/50 rounded-full px-4 py-2">
            <div className="flex items-center gap-1 text-yellow-500">
              <Coins className="h-5 w-5" />
              <span className="font-bold">{currency?.coins?.toLocaleString() || 0}</span>
            </div>
            <div className="w-px h-4 bg-border" />
            <div className="flex items-center gap-1 text-blue-500">
              <Diamond className="h-5 w-5" />
              <span className="font-bold">{currency?.gems?.toLocaleString() || 0}</span>
            </div>
          </div>
        </div>

        {/* Collection Summary */}
        <CollectionSummary 
          ownedByCategory={collectionStats.ownedByCategory}
          totalByCategory={collectionStats.totalByCategory}
        />

        {/* Main Content with Preview Panel */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Preview Panel - Sidebar on desktop */}
          <div className="hidden lg:block lg:col-span-1">
            <ShopPreviewPanel
              previewTheme={previewTheme}
              previewPieceStyle={previewPieceStyle}
              previewAccessory={previewAccessory}
              previewFrame={previewFrame}
              currentTheme={boardTheme}
              currentPieceStyle={pieceStyle as PieceStyle}
              currentAccessory={avatarAccessory}
              currentFrame={profileFrame}
              onReset={resetPreview}
              isPreviewActive={isPreviewActive}
            />
          </div>

          {/* Shop Items */}
          <div className="lg:col-span-3 space-y-4">
            {/* Category Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                <TabsList className="grid w-full sm:w-auto grid-cols-4">
                  {categories.map(cat => (
                    <TabsTrigger key={cat} value={cat} className="flex items-center gap-2">
                      {categoryIcons[cat]}
                      <span className="hidden md:inline">{categoryLabels[cat]}</span>
                    </TabsTrigger>
                  ))}
                </TabsList>

                {/* Filter Toggle */}
                <ToggleGroup 
                  type="single" 
                  value={filter} 
                  onValueChange={(value) => value && setFilter(value as typeof filter)}
                  className="bg-muted/50 rounded-lg p-1"
                >
                  <ToggleGroupItem value="all" size="sm" className="text-xs px-3">
                    All
                  </ToggleGroupItem>
                  <ToggleGroupItem value="owned" size="sm" className="text-xs px-3">
                    <Check className="h-3 w-3 mr-1" />
                    Owned
                  </ToggleGroupItem>
                  <ToggleGroupItem value="not_owned" size="sm" className="text-xs px-3">
                    <Filter className="h-3 w-3 mr-1" />
                    Available
                  </ToggleGroupItem>
                </ToggleGroup>
              </div>

              {categories.map(category => (
                <TabsContent key={category} value={category} className="mt-6">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                    {loading ? (
                      Array.from({ length: 6 }).map((_, i) => (
                        <Skeleton key={i} className="h-64" />
                      ))
                    ) : (
                      getFilteredItems(category).map(item => (
                        <ShopItemCard
                          key={item.id}
                          item={item}
                          isOwned={isOwned(item.id)}
                          isEquipped={isEquipped(item.id)}
                          onPurchase={() => setConfirmItem(item)}
                          onEquip={() => handleEquip(item)}
                          onPreview={() => handlePreview(item)}
                          purchasing={purchasing}
                        />
                      ))
                    )}
                  </div>
                  
                  {!loading && getFilteredItems(category).length === 0 && (
                    <div className="text-center py-12 text-muted-foreground">
                      <ShoppingBag className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p>
                        {filter === 'owned' 
                          ? "You don't own any items in this category yet"
                          : filter === 'not_owned'
                            ? "You own all items in this category!"
                            : "No items available in this category yet"
                        }
                      </p>
                    </div>
                  )}
                </TabsContent>
              ))}
            </Tabs>
          </div>
        </div>

        {/* Mobile Preview Panel */}
        {isPreviewActive && (
          <div className="lg:hidden fixed bottom-0 left-0 right-0 p-4 bg-background/95 backdrop-blur-lg border-t shadow-lg z-50 animate-preview-appear">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3">
                <Eye className="h-5 w-5 text-primary" />
                <span className="font-medium">Previewing: {previewTheme?.name || 'Item'}</span>
              </div>
              <Button variant="outline" size="sm" onClick={resetPreview}>
                Close
              </Button>
            </div>
          </div>
        )}

        {/* Purchase Confirmation Dialog */}
        <Dialog open={!!confirmItem} onOpenChange={() => setConfirmItem(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Confirm Purchase</DialogTitle>
              <DialogDescription>
                Are you sure you want to buy <strong>{confirmItem?.name}</strong>?
              </DialogDescription>
            </DialogHeader>
            
            <div className="py-4">
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <span className="text-muted-foreground">Price:</span>
                <div className="flex items-center gap-3">
                  {confirmItem?.price_coins ? (
                    <div className="flex items-center gap-1 text-yellow-500">
                      <Coins className="h-5 w-5" />
                      <span className="font-bold">{confirmItem.price_coins}</span>
                    </div>
                  ) : null}
                  {confirmItem?.price_gems ? (
                    <div className="flex items-center gap-1 text-blue-500">
                      <Diamond className="h-5 w-5" />
                      <span className="font-bold">{confirmItem.price_gems}</span>
                    </div>
                  ) : null}
                  {confirmItem?.price_coins === 0 && confirmItem?.price_gems === 0 && (
                    <Badge variant="secondary">Free</Badge>
                  )}
                </div>
              </div>
              
              <div className="flex items-center justify-between p-4 mt-2 bg-muted/50 rounded-lg">
                <span className="text-muted-foreground">Your Balance:</span>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-1 text-yellow-500">
                    <Coins className="h-4 w-4" />
                    <span>{currency?.coins || 0}</span>
                  </div>
                  <div className="flex items-center gap-1 text-blue-500">
                    <Diamond className="h-4 w-4" />
                    <span>{currency?.gems || 0}</span>
                  </div>
                </div>
              </div>
            </div>
            
            <DialogFooter>
              <Button variant="outline" onClick={() => setConfirmItem(null)}>
                Cancel
              </Button>
              <Button 
                onClick={handlePurchase}
                disabled={purchasing}
                className="bg-gradient-to-r from-yellow-500 to-amber-500"
              >
                {purchasing ? 'Purchasing...' : 'Confirm Purchase'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
